import { ChangeDetectionStrategy, Component, type OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantLogoComponent } from '@/cloud/components/tenant-logo/tenant-logo.component';
import { tenantLogoUrl } from '@/cloud/models/tenant.model';
import { TranslationService } from '@/layout/service/translation.service';
import { TenantLogoUploadComponent } from '@/cloud/components/tenant-logo/tenant-logo-upload.component';
import { NamespaceWorkingHours } from './namespace-working-hours';
import { NamespaceFeedbackSettings } from './namespace-feedback-settings';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

/** Namespace settings: rename, logo, working hours for room analytics, room ratings on/off, and guarded delete with type-the-name confirmation. */
@Component({
    selector: 'app-namespace-settings',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, MessageModule, TenantLogoComponent, TenantLogoUploadComponent, NamespaceWorkingHours, NamespaceFeedbackSettings, SectionCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-section.scss',
    template: `
        <app-section-card title="General" subtitle="The tenant's display name. Its key is permanent and used in URLs and API calls." icon="pi pi-building">
            <div class="ns-form">
                <div class="ns-field">
                    <label for="tenant-name">Name</label>
                    <div class="ns-inline">
                        <input id="tenant-name" pInputText [(ngModel)]="name" [disabled]="saving()" (keyup.enter)="rename()" />
                        <p-button label="Rename" icon="pi pi-pencil" (onClick)="rename()" [loading]="saving()" [disabled]="saving() || !name().trim() || name().trim() === currentName()" />
                    </div>
                    <small
                        >Key: <code>{{ namespaceKey() }}</code> (permanent)</small
                    >
                </div>
            </div>
        </app-section-card>

        <app-section-card [title]="t()('tenant.logo')" [subtitle]="t()('tenant.logoHelp')" icon="pi pi-image">
            <div class="ns-form">
                <div class="ns-identity">
                    <app-tenant-logo [tenantId]="namespaceId()" [blobId]="previewUploaded() ? currentBlob() : null" [logoUrl]="previewUrl()" (loadError)="previewFailed.set(true)" style="--tenant-logo-size: 4rem" />
                    <div>
                        <strong>{{ currentName() }}</strong>
                        <small class="text-color-secondary">{{ t()('tenant.logoFallback') }}</small>
                    </div>
                </div>
                <div class="ns-actions">
                    <p-button [label]="t()('tenant.uploadLogo')" icon="fa fa-cloud-arrow-up" (onClick)="showLogoUpload.set(true)" [disabled]="logoSaving()" />
                    <p-button [label]="t()('tenant.removeLogo')" icon="fa fa-trash" severity="danger" [outlined]="true" (onClick)="saveLogo(true)" [disabled]="logoSaving() || (!currentLogo() && !currentBlob())" />
                </div>
                @if (previewFailed()) {
                    <p-message severity="warn">{{ t()('tenant.logoUnavailable') }}</p-message>
                }
                <details>
                    <summary class="cursor-pointer text-sm text-color-secondary">{{ t()('tenant.useLogoUrl') }}</summary>
                    <div class="ns-form mt-3">
                        <div class="ns-field">
                            <label for="tenant-logo-url">{{ t()('tenant.logoUrl') }}</label>
                            <input
                                id="tenant-logo-url"
                                pInputText
                                type="url"
                                class="w-full"
                                [ngModel]="logoUrl()"
                                (ngModelChange)="logoUrl.set($event); previewFailed.set(false)"
                                [disabled]="logoSaving()"
                                placeholder="https://example.com/logo.png"
                                maxlength="2048"
                                aria-describedby="tenant-logo-hint"
                            />
                            <small id="tenant-logo-hint">{{ t()('tenant.logoUrlHint') }}</small>
                        </div>
                        @if (logoInvalid()) {
                            <p-message severity="error">{{ t()('tenant.logoInvalid') }}</p-message>
                        }
                        <div class="ns-actions">
                            <p-button [label]="t()('tenant.previewLogo')" icon="fa fa-eye" severity="secondary" [outlined]="true" (onClick)="previewLogo()" [disabled]="logoSaving() || logoInvalid()" />
                            <p-button [label]="t()('tenant.saveLogo')" icon="fa fa-check" (onClick)="saveLogo()" [loading]="logoSaving()" [disabled]="logoSaving() || logoInvalid() || normalizedLogo() === currentLogo()" />
                        </div>
                    </div>
                </details>
            </div>
        </app-section-card>
        @if (showLogoUpload()) {
            <app-tenant-logo-upload [namespaceId]="namespaceId()" [tenantName]="currentName()" (closed)="showLogoUpload.set(false)" />
        }

        @if (namespaceId()) {
            <app-namespace-working-hours [namespaceId]="namespaceId()" />
            <app-namespace-feedback-settings [namespaceId]="namespaceId()" />
        }

        <app-section-card title="Danger zone" subtitle="Deleting a tenant removes its locations, API keys and member access. It is refused while devices or packages still belong to it." icon="pi pi-exclamation-triangle" tone="danger">
            <div class="ns-form">
                @if (deleteBlockers().length > 0) {
                    <p-message severity="warn">{{ blockerText() }}</p-message>
                }
                <div class="ns-field">
                    <label for="tenant-delete-confirm"
                        >Type <strong>{{ currentName() }}</strong> to confirm</label
                    >
                    <div class="ns-inline">
                        <input id="tenant-delete-confirm" pInputText [(ngModel)]="deleteConfirmation" [disabled]="deleting()" placeholder="Tenant name" autocomplete="off" />
                        <p-button label="Delete tenant" icon="pi pi-trash" severity="danger" (onClick)="deleteNamespace()" [loading]="deleting()" [disabled]="deleting() || deleteConfirmation() !== currentName()" />
                    </div>
                </div>
            </div>
        </app-section-card>
    `
})
export class NamespaceSettings implements OnInit {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private messageService = inject(MessageService);
    readonly t = inject(TranslationService).t;

    readonly namespaceId = signal('');
    readonly showLogoUpload = signal(false);
    readonly previewUploaded = signal(true);
    name = signal('');
    saving = signal(false);
    deleting = signal(false);
    deleteConfirmation = signal('');
    deleteBlockers = signal<{ type: string; count: number }[]>([]);
    readonly logoUrl = signal('');
    readonly logoSaving = signal(false);
    readonly previewUrl = signal<string | null>(null);
    readonly previewFailed = signal(false);
    readonly normalizedLogo = computed(() => tenantLogoUrl(this.logoUrl()));
    readonly logoInvalid = computed(() => !!this.logoUrl().trim() && !this.normalizedLogo());

    private namespace = computed(() => (this.api.namespaces() ?? []).find((n) => (n.id ?? n.Id) === this.namespaceId()) ?? null);
    currentName = computed(() => this.namespace()?.name ?? this.namespace()?.Name ?? '');
    namespaceKey = computed(() => this.namespace()?.key ?? this.namespace()?.Key ?? '');
    readonly currentLogo = computed(() => this.namespace()?.logoUrl ?? this.namespace()?.LogoUrl ?? null);
    readonly currentBlob = computed(() => this.namespace()?.logoBlobId ?? this.namespace()?.LogoBlobId ?? null);

    constructor() {
        this.route.parent?.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
            this.namespaceId.set(params.get('namespaceId') ?? '');
            this.showLogoUpload.set(false);
            this.saving.set(false);
            this.logoSaving.set(false);
            this.deleting.set(false);
            this.deleteConfirmation.set('');
            this.deleteBlockers.set([]);
        });
        effect(() => {
            this.namespaceId();
            this.name.set(this.currentName());
        });
        effect(() => {
            this.namespaceId();
            this.currentBlob();
            this.previewUploaded.set(true);
            this.logoUrl.set(this.currentLogo() ?? '');
            this.previewUrl.set(this.currentLogo());
            this.previewFailed.set(false);
        });
    }

    blockerText = computed(
        () =>
            'Cannot delete yet: ' +
            this.deleteBlockers()
                .map((b) => `${b.count} ${b.count === 1 ? b.type.replace(/s$/, '') : b.type}`)
                .join(', ') +
            ' still in this tenant.'
    );

    ngOnInit(): void {
        this.api.getNamespaces().catch((err) => this.toastError(this.t()('tenant.settingsLoadError'), err));
    }

    previewLogo() {
        if (this.logoInvalid()) return;
        this.previewFailed.set(false);
        this.previewUploaded.set(false);
        this.previewUrl.set(this.normalizedLogo());
    }

    async saveLogo(remove = false) {
        if (this.logoSaving() || (!remove && this.logoInvalid())) return;
        const id = this.namespaceId();
        this.logoSaving.set(true);
        try {
            await this.api.updateNamespaceLogo(id, remove ? null : this.normalizedLogo());
            if (id === this.namespaceId()) {
                this.messageService.add({ severity: 'success', summary: this.t()('tenant.logoSaved') });
            }
        } catch (err: unknown) {
            this.toastError(this.t()('tenant.logoSaveError'), err);
        } finally {
            if (id === this.namespaceId()) this.logoSaving.set(false);
        }
    }

    rename() {
        const newName = this.name().trim();
        if (!newName || newName === this.currentName()) return;
        const id = this.namespaceId();
        this.saving.set(true);
        this.api
            .updateNamespace(id, newName)
            .then(() => this.messageService.add({ severity: 'success', summary: 'Tenant Renamed', detail: newName }))
            .catch((err) => this.toastError('Rename Failed', err))
            .finally(() => {
                if (id === this.namespaceId()) this.saving.set(false);
            });
    }

    deleteNamespace() {
        if (this.deleteConfirmation() !== this.currentName()) return;
        const id = this.namespaceId();
        const name = this.currentName();
        this.deleting.set(true);
        this.deleteBlockers.set([]);
        this.api
            .deleteNamespace(id)
            .then(() => {
                this.messageService.add({ severity: 'success', summary: 'Tenant Deleted', detail: name });
                if (id === this.namespaceId()) this.router.navigate(['/admin/tenants']);
            })
            .catch((err) => {
                if (id === this.namespaceId() && err?.status === 409 && err?.error?.blockers) {
                    this.deleteBlockers.set(err.error.blockers);
                } else {
                    this.toastError('Delete Failed', err);
                }
            })
            .finally(() => {
                if (id === this.namespaceId()) this.deleting.set(false);
            });
    }

    private toastError(summary: string, err: any) {
        const detail = err?.error?.message ?? (typeof err?.error === 'string' ? err.error : null) ?? err?.message ?? 'An error occurred';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
